// Two paths, one contract (POST /api/admin/analytics/events):
//  - normal flushes ride the shared `axiosRubyClient` instance (the same
//    client `featureFlagsAPI.me()` uses for /api/admin/*) → per-request base
//    URL, Authorization header, and the 401→refresh interceptor for free.
//    NEVER fetch with a hardcoded host: a review app or QA would send its
//    events to production. The keepalive path below reads
//    `getRestEndpoint()` at send time, the same source the axios
//    interceptor uses (src/util/axios/axiosClient.ts). axiosRubyClient has
//    no defaults.baseURL, so reading that instead sent unload batches to
//    the admin host, where they were lost.
//  - unload flushes use fetch keepalive (axios can't outlive the page),
//    byte-capped: oversized bodies reject instantly and silently, so the
//    batch is trimmed to fit and the remainder is returned for the caller
//    to spill.
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { GraphyAdminEndpoints } from '@/constants/urls';
import { getRestEndpoint } from '@/runtimeConfig';
import ls, { ACCESS_TOKEN_KEY } from '@/util/localstorage';
import { AnalyticsEnvelope } from './envelope';

const ENDPOINT = GraphyAdminEndpoints.ANALYTICS_EVENTS;
export const KEEPALIVE_BYTE_CAP = 50 * 1024;
// Mirrors the server's MAX_BATCH (app/controllers/api/admin/analytics_controller.rb)
// — a keepalive body over this event count 422s and the whole batch is lost
// silently (fetch keepalive gives no error surface on unload), so the count
// cap has to be enforced here too, alongside the byte cap.
export const MAX_BATCH_EVENTS = 50;

export interface FlushBody {
  events: AnalyticsEnvelope[];
  _meta?: { dropped_since_last: number };
}

export async function post(body: FlushBody): Promise<boolean> {
  try {
    await axiosRubyClient.post(ENDPOINT, body);
    return true;
  } catch {
    return false;
  }
}

// Fire-and-forget; trims the batch to fit the keepalive budget and returns
// the events that did NOT fit (caller spills them).
export function postOnUnload(body: FlushBody): AnalyticsEnvelope[] {
  const kept: AnalyticsEnvelope[] = [];
  const overflow: AnalyticsEnvelope[] = [];
  let bytes = 0;
  for (const event of body.events) {
    const size = JSON.stringify(event).length;
    if (kept.length >= MAX_BATCH_EVENTS || bytes + size > KEEPALIVE_BYTE_CAP) {
      overflow.push(event);
    } else {
      kept.push(event);
      bytes += size;
    }
  }
  if (kept.length === 0) return overflow;
  try {
    const base = getRestEndpoint().replace(/\/+$/, '');
    const token = ls.get(ACCESS_TOKEN_KEY);
    // Mirrors axiosClient.ts's request interceptor exactly (lowercase
    // `bearer` — NOT `Bearer` — is what the backend actually receives on
    // every other /api/admin/* request).
    void fetch(`${base}${ENDPOINT}`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `bearer ${token ?? ''}`,
      },
      body: JSON.stringify({ ...body, events: kept }),
    });
  } catch {
    return body.events; // nothing sent — spill everything
  }
  return overflow;
}
